// SupportCenter.jsx - Complete Fixed Version
import React, { useState, useEffect, useRef } from 'react';
import {
  FaTicketAlt,
  FaPlus,
  FaSearch,
  FaFilter,
  FaComments,
  FaPaperclip,
  FaClock,
  FaCheckCircle,
  FaExclamationCircle,
  FaCircle,
  FaFileAlt,
  FaPaperPlane,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaCalendarAlt,
  FaSpinner,
  FaDownload,
  FaPhone,
  FaArrowRight,
  FaTag,
  FaRegClock,
  FaLock,
  FaBell,
  FaCommentDots,
  FaInfoCircle,
  FaFolderOpen,
  FaImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileArchive,
  FaExpand,
} from 'react-icons/fa';
import withAuth from '../../auth/WithAuth';
import './helpdesk.scss';
import Nav from '../../../components/layout/Nav';
import { toast } from 'react-toastify';
import { serverUrlAuth } from '../../../serverUrl';

const STATUSES = [
  { value: 'open', label: 'Open', icon: '🟢', color: '#3b82f6' },
  { value: 'pending', label: 'Pending', icon: '🟡', color: '#f59e0b' },
  { value: 'resolved', label: 'Resolved', icon: '✅', color: '#10b981' },
  { value: 'closed', label: 'Closed', icon: '🔵', color: '#6b7280' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: '#6b7280' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#ef4444' },
  { value: 'urgent', label: 'Urgent', color: '#dc2626' },
];

const ISSUE_TYPES = [
  { value: 'complaint', label: 'Complaint', icon: '⚠️' },
  { value: 'suggestion', label: 'Suggestion', icon: '💡' },
];

const CATEGORIES = [
  { value: 'account', label: 'My Account', icon: '👤' },
  { value: 'projects', label: 'Projects', icon: '📁' },
  { value: 'proposals', label: 'Proposals', icon: '📄' },
  { value: 'payments', label: 'Payments', icon: '💳' },
  { value: 'verification', label: 'Verification', icon: '✅' },
  { value: 'technical', label: 'Technical Issue', icon: '🔧' },
  { value: 'password', label: 'Password Reset', icon: '🔑' },
  { value: 'notifications', label: 'Notifications', icon: '🔔' },
  { value: 'other', label: 'Other', icon: '📌' },
];

// Chat polling interval (5 seconds)
const CHAT_POLLING_INTERVAL = 5000;

const SupportCenter = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [newMessage, setNewMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTicketLoading, setIsTicketLoading] = useState(false);

  // Image preview states
  const [previewImage, setPreviewImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  // Chat polling states
  const [isPollingChat, setIsPollingChat] = useState(true);
  const [lastChatUpdate, setLastChatUpdate] = useState(null);
  const [newRepliesCount, setNewRepliesCount] = useState(0);
  const [hasNewReplies, setHasNewReplies] = useState(false);

  // Refs
  const chatPollingTimeoutRef = useRef(null);
  const isChatPollingRef = useRef(true);
  const messageCountRef = useRef(0);
  const chatContainerRef = useRef(null);

  const [newTicket, setNewTicket] = useState({
    issue_type: '',
    category: '',
    subject: '',
    description: '',
    priority: 'medium',
    attachments: [],
    call_me: false,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchTickets();
    startChatPolling();

    return () => {
      stopChatPolling();
    };
  }, []);

  // ============ CHAT POLLING ============
  const startChatPolling = () => {
    isChatPollingRef.current = true;
    setIsPollingChat(true);
    if (chatPollingTimeoutRef.current) {
      clearTimeout(chatPollingTimeoutRef.current);
    }
    pollForChatUpdates();
  };

  const stopChatPolling = () => {
    isChatPollingRef.current = false;
    setIsPollingChat(false);
    if (chatPollingTimeoutRef.current) {
      clearTimeout(chatPollingTimeoutRef.current);
      chatPollingTimeoutRef.current = null;
    }
  };

  const pollForChatUpdates = async () => {
    if (!isChatPollingRef.current) return;

    if (selectedTicket && showTicketDetails) {
      try {
        const response = await serverUrlAuth.get(
          `helpdesk/tickets/${selectedTicket.id}/`,
        );
        const updatedTicket = response.data;

        const oldReplyCount = selectedTicket.replies ? selectedTicket.replies.length : 0;
        const newReplyCount = updatedTicket.replies ? updatedTicket.replies.length : 0;

        if (newReplyCount > oldReplyCount) {
          const newMessages = newReplyCount - oldReplyCount;
          
          setSelectedTicket(updatedTicket);
          setLastChatUpdate(new Date());
          setHasNewReplies(true);
          setNewRepliesCount(prev => prev + newMessages);
          
          setTickets(prev =>
            prev.map(t =>
              t.id === updatedTicket.id ? updatedTicket : t
            )
          );

          if (newMessages === 1) {
            toast.info(`💬 New reply from support in ticket ${updatedTicket.ticket_number}`, {
              position: "top-right",
              autoClose: 4000,
            });
          } else {
            toast.info(`💬 ${newMessages} new replies from support in ticket ${updatedTicket.ticket_number}`, {
              position: "top-right",
              autoClose: 4000,
            });
          }

          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          }, 100);
        }
      } catch (err) {
        console.warn('Chat polling error:', err);
      }
    }

    if (isChatPollingRef.current) {
      chatPollingTimeoutRef.current = setTimeout(pollForChatUpdates, CHAT_POLLING_INTERVAL);
    }
  };

  // ============ FETCH FUNCTIONS ============
  const fetchTickets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await serverUrlAuth.get('helpdesk/tickets/');
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load tickets');
      toast.error('Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  };

  // ============ FILTERING ============
  useEffect(() => {
    let filtered = tickets;
    if (statusFilter !== 'all') {
      filtered = filtered.filter((ticket) => ticket.status === statusFilter);
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    setFilteredTickets(filtered);
    setCurrentPage(1);
  }, [tickets, searchTerm, statusFilter]);

  // ============ HANDLERS ============
  const getStatusColor = (status) => {
    const found = STATUSES.find((s) => s.value === status);
    return found ? found.color : '#6b7280';
  };

  const getStatusLabel = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || '';
  };

  const getPriorityBadge = (priority) => {
    const classes = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent',
    };
    return `priority-badge ${classes[priority] || 'priority-low'}`;
  };

  const getStatusBadge = (status) => {
    const classes = {
      open: 'status-open',
      pending: 'status-pending',
      resolved: 'status-resolved',
      closed: 'status-closed',
    };
    return `status-badge ${classes[status] || 'status-open'}`;
  };

  const getCategoryLabel = (category) => {
    const found = CATEGORIES.find((c) => c.value === category);
    return found ? found.label : category;
  };

  const getCategoryIcon = (category) => {
    const found = CATEGORIES.find((c) => c.value === category);
    return found ? found.icon : '📌';
  };

  const getIssueTypeLabel = (type) => {
    const found = ISSUE_TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // ============ IMAGE PREVIEW HANDLERS ============
  const handleImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImage(null);
  };

  // ============ FILE TYPE DETECTION ============
  const getFileIcon = (filename) => {
    const extension = filename?.split('.').pop()?.toLowerCase() || '';
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
    const pdfExtensions = ['pdf'];
    const wordExtensions = ['doc', 'docx'];
    const excelExtensions = ['xls', 'xlsx', 'csv'];
    const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz'];

    if (imageExtensions.includes(extension)) return { icon: FaImage, type: 'image' };
    if (pdfExtensions.includes(extension)) return { icon: FaFilePdf, type: 'pdf' };
    if (wordExtensions.includes(extension)) return { icon: FaFileWord, type: 'word' };
    if (excelExtensions.includes(extension)) return { icon: FaFileExcel, type: 'excel' };
    if (archiveExtensions.includes(extension)) return { icon: FaFileArchive, type: 'archive' };
    return { icon: FaFileAlt, type: 'other' };
  };

  const isImageFile = (filename) => {
    const extension = filename?.split('.').pop()?.toLowerCase() || '';
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
    return imageExtensions.includes(extension);
  };

  const handleViewTicket = async (ticketId) => {
    try {
      setIsTicketLoading(true);
      const response = await serverUrlAuth.get(`helpdesk/tickets/${ticketId}/`);
      setSelectedTicket(response.data);
      setShowTicketDetails(true);
      setHasNewReplies(false);
      setNewRepliesCount(0);
      messageCountRef.current = response.data.replies ? response.data.replies.length : 0;
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 200);
    } catch (err) {
      console.error('Failed to load ticket:', err);
      toast.error('Failed to load ticket details');
    } finally {
      setIsTicketLoading(false);
    }
  };

  const handleCloseTicketDetails = () => {
    setShowTicketDetails(false);
    setSelectedTicket(null);
    setNewMessage('');
    setHasNewReplies(false);
    setNewRepliesCount(0);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setIsSubmitting(true);
    try {
      await serverUrlAuth.post(`helpdesk/tickets/${selectedTicket.id}/reply/`, {
        message: newMessage.trim(),
      });
      
      const updatedTicket = await serverUrlAuth.get(
        `helpdesk/tickets/${selectedTicket.id}/`,
      );
      setSelectedTicket(updatedTicket.data);
      setHasNewReplies(false);
      setNewRepliesCount(0);
      
      setTickets((prev) =>
        prev.map((t) =>
          t.id === updatedTicket.data.id ? updatedTicket.data : t,
        ),
      );
      setNewMessage('');
      toast.success('Reply sent successfully');
      
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!newTicket.issue_type) errors.issue_type = 'Issue type is required';
    if (!newTicket.category) errors.category = 'Category is required';
    if (!newTicket.subject.trim()) errors.subject = 'Subject is required';
    if (!newTicket.description.trim()) errors.description = 'Description is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('issue_type', newTicket.issue_type);
      formData.append('category', newTicket.category);
      formData.append('subject', newTicket.subject);
      formData.append('description', newTicket.description);
      formData.append('priority', newTicket.priority);
      formData.append('call_me', newTicket.call_me);

      if (newTicket.attachments.length > 0) {
        newTicket.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }

      const response = await serverUrlAuth.post(
        'helpdesk/tickets/create/',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      setTickets([response.data, ...tickets]);
      setShowCreateTicket(false);
      setNewTicket({
        issue_type: '',
        category: '',
        subject: '',
        description: '',
        priority: 'medium',
        attachments: [],
        call_me: false,
      });
      setFormErrors({});
      toast.success('Ticket created successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewTicket({
      ...newTicket,
      attachments: [...newTicket.attachments, ...files],
    });
  };

  const removeAttachment = (index) => {
    setNewTicket({
      ...newTicket,
      attachments: newTicket.attachments.filter((_, i) => i !== index),
    });
  };

  const handleDownloadAttachment = (attachmentUrl, filename) => {
    window.open(attachmentUrl, '_blank');
  };

  const calculateStats = (ticketsList) => {
    return {
      total: ticketsList.length,
      open: ticketsList.filter((t) => t.status === 'open').length,
      pending: ticketsList.filter((t) => t.status === 'pending').length,
      resolved: ticketsList.filter((t) => t.status === 'resolved').length,
      closed: ticketsList.filter((t) => t.status === 'closed').length,
    };
  };

  const displayStats = calculateStats(tickets);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  if (isLoading && tickets.length === 0) {
    return (
      <>
        <Nav />
        <div className="support-center">
          <div className="loading-container">
            <div className="loading-spinner">
              <FaSpinner className="spinner" />
            </div>
            <p>Loading your tickets...</p>
          </div>
        </div>
      </>
    );
  }

  if (error && tickets.length === 0) {
    return (
      <>
        <Nav />
        <div className="support-center">
          <div className="error-container">
            <FaExclamationCircle className="error-icon" />
            <h3>Unable to Load Tickets</h3>
            <p>{error}</p>
            <button onClick={fetchTickets} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Nav />
      <div className="support-center">
        {/* Header */}
        <header className="support-header">
          <div className="container">
            <div className="header-content">
              <div className="header-left">
                <div className="brand-icon">
                  <FaTicketAlt />
                </div>
                <div>
                  <h1>Help Center</h1>
                  <p>Get help with your projects, payments, and account</p>
                </div>
              </div>
              <div className="header-right">
                <button
                  className="btn-primary"
                  onClick={() => setShowCreateTicket(true)}
                >
                  <FaPlus /> New Ticket
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Stats */}
        <section className="stats-section">
          <div className="container">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon total">
                  <FaTicketAlt />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{displayStats.total}</span>
                  <span className="stat-label">Total Tickets</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon open">
                  <FaCircle />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{displayStats.open}</span>
                  <span className="stat-label">Open</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon pending">
                  <FaClock />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{displayStats.pending}</span>
                  <span className="stat-label">Pending</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon resolved">
                  <FaCheckCircle />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{displayStats.resolved}</span>
                  <span className="stat-label">Resolved</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="filters-section">
          <div className="container">
            <div className="filters-wrapper">
              <div className="search-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search tickets by number, subject, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-wrapper">
                <FaFilter className="filter-icon" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  {STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.icon} {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Ticket Grid */}
        <section className="tickets-section">
          <div className="container">
            {currentItems.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <FaFolderOpen />
                </div>
                <h3>No tickets found</h3>
                <p>
                  {searchTerm || statusFilter !== 'all'
                    ? 'No tickets match your current filter criteria.'
                    : "You haven't created any tickets yet. Click 'New Ticket' to get started."}
                </p>
              </div>
            ) : (
              <>
                <div className="ticket-grid">
                  {currentItems.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="ticket-card"
                      onClick={() => handleViewTicket(ticket.id)}
                    >
                      <div className="ticket-card-header">
                        <div className="ticket-number-badge">
                          <FaTicketAlt />
                          <span>{ticket.ticket_number}</span>
                        </div>
                        <div className="ticket-status-indicator">
                          <span className={`status-dot ${ticket.status}`} />
                          <span className={getStatusBadge(ticket.status)}>
                            {getStatusLabel(ticket.status)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="ticket-card-body">
                        <h4 className="ticket-subject">{ticket.subject}</h4>
                        <p className="ticket-description-preview">
                          {ticket.description?.substring(0, 100)}
                          {ticket.description?.length > 100 && '...'}
                        </p>
                      </div>
                      
                      <div className="ticket-card-footer">
                        <div className="ticket-meta">
                          <span className="meta-item">
                            <span className="meta-icon">{getCategoryIcon(ticket.category)}</span>
                            {getCategoryLabel(ticket.category)}
                          </span>
                          <span className="meta-item">
                            <FaCalendarAlt />
                            {formatDateShort(ticket.created_at)}
                          </span>
                          {ticket.attachments && ticket.attachments.length > 0 && (
                            <span className="meta-item">
                              <FaPaperclip />
                              {ticket.attachments.length}
                            </span>
                          )}
                          {ticket.replies && ticket.replies.length > 0 && (
                            <span className="meta-item replies">
                              <FaCommentDots />
                              {ticket.replies.length}
                            </span>
                          )}
                        </div>
                        <div className="ticket-priority">
                          <span className={getPriorityBadge(ticket.priority)}>
                            {ticket.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="page-btn"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <FaChevronLeft />
                    </button>
                    <span className="page-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className="page-btn"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ===== CREATE TICKET MODAL - COMPLETE FIXED VERSION ===== */}
        {showCreateTicket && (
          <div 
            className="modal-overlay" 
            onClick={() => setShowCreateTicket(false)}
          >
            <div 
              className="modal-content create-ticket-modal" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-header-content">
                  <div className="modal-icon">
                    <FaTicketAlt />
                  </div>
                  <div>
                    <h2>Create Ticket</h2>
                    <p>We'll get back to you as soon as possible</p>
                  </div>
                </div>
                <button 
                  className="modal-close" 
                  onClick={() => setShowCreateTicket(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="modal-body">
                {/* Issue Category */}
                <div className="form-group">
                  <label>
                    Issue Category <span className="required">*</span>
                  </label>
                  <div className="radio-group">
                    {ISSUE_TYPES.map((type) => (
                      <label
                        key={type.value}
                        className={`radio-option ${newTicket.issue_type === type.value ? 'active' : ''}`}
                      >
                        <input
                          type="radio"
                          name="issue_type"
                          value={type.value}
                          checked={newTicket.issue_type === type.value}
                          onChange={(e) =>
                            setNewTicket({ ...newTicket, issue_type: e.target.value })
                          }
                        />
                        <span>{type.icon}</span>
                        {type.label}
                      </label>
                    ))}
                  </div>
                  {formErrors.issue_type && (
                    <span className="error-message">{formErrors.issue_type}</span>
                  )}
                </div>

                {/* Select Topic */}
                <div className="form-group">
                  <label>
                    Select Topic <span className="required">*</span>
                  </label>
                  <select
                    value={newTicket.category}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, category: e.target.value })
                    }
                    className={formErrors.category ? 'error' : ''}
                  >
                    <option value="">Select a topic</option>
                    {CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.icon} {category.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && (
                    <span className="error-message">{formErrors.category}</span>
                  )}
                </div>

                {/* Subject */}
                <div className="form-group">
                  <label>
                    Subject <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, subject: e.target.value })
                    }
                    placeholder="Brief description of your issue"
                    className={formErrors.subject ? 'error' : ''}
                  />
                  {formErrors.subject && (
                    <span className="error-message">{formErrors.subject}</span>
                  )}
                </div>

                {/* Description */}
                <div className="form-group">
                  <label>
                    Description <span className="required">*</span>
                  </label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, description: e.target.value })
                    }
                    placeholder="Describe the issue clearly (No passwords, please)"
                    rows="4"
                    className={formErrors.description ? 'error' : ''}
                  />
                  {formErrors.description && (
                    <span className="error-message">{formErrors.description}</span>
                  )}
                </div>

                {/* Attachments */}
                <div className="form-group">
                  <label>Attachments (optional)</label>
                  <div className="file-upload">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      id="file-upload"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                    />
                    <label htmlFor="file-upload">
                      <FaPaperclip />
                      <span>Upload files</span>
                      <small>Max 5 files, 10MB each</small>
                    </label>
                  </div>
                  {newTicket.attachments.length > 0 && (
                    <div className="attachment-list">
                      {newTicket.attachments.map((file, index) => (
                        <div key={index} className="attachment-item">
                          <FaFileAlt />
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">
                            ({(file.size / 1024).toFixed(0)} KB)
                          </span>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeAttachment(index)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Call Me Checkbox */}
               

                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => setShowCreateTicket(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="spinner" /> Creating...
                      </>
                    ) : (
                      'Submit Ticket'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===== TICKET DETAILS MODAL ===== */}
        {showTicketDetails && selectedTicket && (
          <div className="modal-overlay" onClick={handleCloseTicketDetails}>
            <div
              className="modal-content ticket-details-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header" style={{ background: '#673200' }}>
                <div className="header-left">
                  <div className="ticket-id">
                    <FaTicketAlt className="ticket-icon" />
                    <span className="ticket-number">{selectedTicket.ticket_number}</span>
                    {hasNewReplies && newRepliesCount > 0 && (
                      <span className="new-replies-badge">
                        <FaBell />
                        {newRepliesCount} new
                      </span>
                    )}
                  </div>
                  <div className="ticket-status-badges">
                    <span className={getStatusBadge(selectedTicket.status)}>
                      {getStatusLabel(selectedTicket.status)}
                    </span>
                    <span className={getPriorityBadge(selectedTicket.priority)}>
                      {selectedTicket.priority}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    className="close-modal"
                    onClick={handleCloseTicketDetails}
                    style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="modal-body two-column">
                {/* Left Column - Details */}
                <div className="details-column">
                  {/* Ticket Info Card */}
                  <div className="info-card">
                    <div className="card-header">
                      <FaInfoCircle className="card-icon" />
                      <h4>Ticket Information</h4>
                    </div>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Issue Type</span>
                        <span className={`issue-type-badge ${selectedTicket.issue_type}`}>
                          {getIssueTypeLabel(selectedTicket.issue_type)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Category</span>
                        <span className="info-value">
                          {getCategoryIcon(selectedTicket.category)} {getCategoryLabel(selectedTicket.category)}
                        </span>
                      </div>
                      <div className="info-item full-width">
                        <span className="info-label">Subject</span>
                        <span className="info-value subject">{selectedTicket.subject}</span>
                      </div>
                      <div className="info-item full-width">
                        <span className="info-label">Description</span>
                        <div className="info-description">
                          {selectedTicket.description}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Meta Info Card */}
                  <div className="info-card">
                    <div className="card-header">
                      <FaCalendarAlt className="card-icon" />
                      <h4>Metadata</h4>
                    </div>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Created</span>
                        <span className="info-value">
                          <FaCalendarAlt className="info-icon" />
                          {formatDate(selectedTicket.created_at)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Last Updated</span>
                        <span className="info-value">
                          <FaCalendarAlt className="info-icon" />
                          {formatDate(selectedTicket.updated_at)}
                        </span>
                      </div>
                      {selectedTicket.resolved_at && (
                        <div className="info-item">
                          <span className="info-label">Resolved</span>
                          <span className="info-value resolved">
                            <FaCheckCircle className="info-icon resolved" />
                            {formatDate(selectedTicket.resolved_at)}
                          </span>
                        </div>
                      )}
                      {selectedTicket.handled_by && (
                        <div className="info-item">
                          <span className="info-label">Handled By</span>
                          <span className="info-value">
                            <FaUser className="info-icon" />
                            {selectedTicket.handled_by_name || 'Staff'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attachments Card */}
                  {selectedTicket.attachments &&
                    selectedTicket.attachments.length > 0 && (
                      <div className="info-card attachments-card">
                        <div className="card-header">
                          <FaPaperclip className="card-icon" />
                          <h4>Attachments ({selectedTicket.attachments.length})</h4>
                        </div>
                        <div className="attachments-grid">
                          {selectedTicket.attachments.map((attachment) => {
                            const filename = attachment.file.split('/').pop();
                            const isImage = isImageFile(filename);
                            const fileInfo = getFileIcon(filename);
                            const FileIcon = fileInfo.icon;

                            return (
                              <div
                                key={attachment.id}
                                className={`attachment-item ${isImage ? 'image' : 'file'}`}
                                onClick={() => {
                                  if (isImage) {
                                    handleImagePreview(attachment.file);
                                  } else {
                                    handleDownloadAttachment(attachment.file, filename);
                                  }
                                }}
                              >
                                {isImage ? (
                                  <div className="attachment-image-thumbnail">
                                    <img src={attachment.file} alt={filename} />
                                    <div className="attachment-overlay">
                                      <FaExpand />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="attachment-file-icon">
                                    <FileIcon />
                                    <span className="file-ext">{filename.split('.').pop()}</span>
                                  </div>
                                )}
                                <div className="attachment-info">
                                  <span className="filename">{filename}</span>
                                  <span className="file-type">
                                    {isImage ? 'Image' : fileInfo.type}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Status Banner */}
                  {selectedTicket.status === 'resolved' && (
                    <div className="status-banner resolved">
                      <FaCheckCircle />
                      <div>
                        <strong>Resolved</strong>
                        <p>This ticket has been resolved. Reply to reopen if needed.</p>
                      </div>
                    </div>
                  )}
                  {selectedTicket.status === 'closed' && (
                    <div className="status-banner closed">
                      <FaTimes />
                      <div>
                        <strong>Closed</strong>
                        <p>This ticket has been closed. Create a new ticket for further assistance.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Chat */}
                <div className="chat-column">
                  <div className="chat-header">
                    <div className="chat-header-left">
                      <FaComments />
                      <span>Conversation</span>
                      {hasNewReplies && newRepliesCount > 0 && (
                        <span className="new-replies-indicator">
                          {newRepliesCount} new
                        </span>
                      )}
                      <span className="message-count">
                        ({selectedTicket.replies ? selectedTicket.replies.length + 1 : 1})
                      </span>
                    </div>
                  </div>

                  <div className="chat-messages" ref={chatContainerRef}>
                    {/* Initial message */}
                    {selectedTicket.description && (
                      <div className="chat-message user">
                        <div className="message-avatar">
                          <FaUser />
                        </div>
                        <div className="message-content">
                          <div className="message-meta">
                            <span className="sender">You</span>
                            <span className="time">
                              {formatDate(selectedTicket.created_at)}
                            </span>
                          </div>
                          <div className="message-text">
                            {selectedTicket.description}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Replies */}
                    {selectedTicket.replies &&
                      selectedTicket.replies.map((reply, index) => {
                        const isNewReply = hasNewReplies && 
                          index >= (selectedTicket.replies.length - newRepliesCount);
                        
                        return (
                          <div
                            key={reply.id}
                            className={`chat-message ${reply.is_staff_reply ? 'staff' : 'user'} ${isNewReply ? 'new-reply' : ''}`}
                          >
                            <div className="message-avatar">
                              {reply.is_staff_reply ? (
                                <FaUser className="staff-avatar" />
                              ) : (
                                <FaUser />
                              )}
                            </div>
                            <div className="message-content">
                              <div className="message-meta">
                                <span className="sender">
                                  {reply.is_staff_reply ? (
                                    <>
                                      <span className="staff-badge">Support</span>
                                      {reply.sender_name || 'Staff'}
                                    </>
                                  ) : (
                                    'You'
                                  )}
                                </span>
                                <span className="time">
                                  {formatDate(reply.created_at)}
                                </span>
                                {isNewReply && (
                                  <span className="new-badge">New</span>
                                )}
                              </div>
                              <div className="message-text">{reply.message}</div>
                              
                              {/* Message Attachments */}
                              {reply.attachments &&
                                reply.attachments.length > 0 && (
                                  <div className="message-attachments-grid">
                                    {reply.attachments.map((att) => {
                                      const filename = att.file.split('/').pop();
                                      const isImage = isImageFile(filename);
                                      const fileInfo = getFileIcon(filename);
                                      const FileIcon = fileInfo.icon;

                                      return (
                                        <div
                                          key={att.id}
                                          className={`message-attachment-item ${isImage ? 'image' : 'file'}`}
                                          onClick={() => {
                                            if (isImage) {
                                              handleImagePreview(att.file);
                                            } else {
                                              handleDownloadAttachment(att.file, filename);
                                            }
                                          }}
                                        >
                                          {isImage ? (
                                            <div className="attachment-thumb">
                                              <img src={att.file} alt={filename} />
                                              <div className="thumb-overlay">
                                                <FaExpand />
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="file-icon-wrapper">
                                              <FileIcon />
                                              <span className="file-ext">
                                                {filename.split('.').pop()}
                                              </span>
                                            </div>
                                          )}
                                          <span className="file-name">{filename}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Reply Input */}
                  {selectedTicket.status !== 'closed' &&
                    selectedTicket.status !== 'resolved' && (
                      <div className="chat-reply">
                        <div className="reply-input-wrapper">
                          <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your reply..."
                            rows="2"
                            className="reply-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                              }
                            }}
                          />
                          <button
                            className="send-btn"
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim() || isSubmitting}
                          >
                            {isSubmitting ? (
                              <FaSpinner className="spinner" />
                            ) : (
                              <>
                                <FaPaperPlane /> Send
                              </>
                            )}
                          </button>
                        </div>
                        <div className="reply-hint">
                          <FaRegClock /> Press Enter to send, Shift+Enter for new line
                        </div>
                      </div>
                    )}

                  {/* Resolved/Closed Reply Disabled */}
                  {(selectedTicket.status === 'resolved' ||
                    selectedTicket.status === 'closed') && (
                    <div className="chat-disabled">
                      <FaLock />
                      <span>
                        {selectedTicket.status === 'resolved'
                          ? 'This ticket has been resolved'
                          : 'This ticket has been closed'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {showImagePreview && previewImage && (
          <div className="image-preview-overlay" onClick={closeImagePreview}>
            <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
              <button className="image-preview-close" onClick={closeImagePreview}>
                <FaTimes />
              </button>
              <img src={previewImage} alt="Preview" />
             
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default withAuth(SupportCenter, 'client');